import type { TaxonomyClass, VerificationScopeType } from "@sitescene/types";

import { useState } from "react";

import { Button, Field, Select, TextInput } from "@/components/field";
import { type PriorWriteInput } from "@/features/plausibility/use-priors";

import {
	draftLogOdds,
	draftToWriteInput,
	EMPTY_DRAFT,
	HOUR_BUCKETS,
	isDraftValid,
	type PriorDraft,
	SEASONS,
} from "./prior-draft";

const SCOPES: Exclude<VerificationScopeType, "global">[] = [
	"installation",
	"zone",
	"sensor",
];

export interface PriorFormProps {
	busy: boolean;
	classes: TaxonomyClass[];
	error: Error | null;
	onSubmit: (
		input: PriorWriteInput & {
			scopeRef: string;
			scopeType: Exclude<VerificationScopeType, "global">;
		},
	) => void;
	scoped?: boolean;
}

/** Adds a prior. Existing rows are edited in the table, not here. */
export const PriorForm = ({
	busy,
	classes,
	error,
	onSubmit,
	scoped = false,
}: PriorFormProps) => {
	const prefix = scoped ? "tenant" : "global";
	const [draft, setDraft] = useState<PriorDraft>(EMPTY_DRAFT);
	const [scopeType, setScopeType] =
		useState<Exclude<VerificationScopeType, "global">>("installation");
	const [scopeRef, setScopeRef] = useState("");
	const patch = (changes: Partial<PriorDraft>) =>
		setDraft((current) => ({ ...current, ...changes }));

	const valid = isDraftValid(draft, classes) && (!scoped || scopeRef.trim());
	const logPriorOdds = draftLogOdds(draft);

	return (
		<form
			className="flex flex-wrap items-end gap-3"
			onSubmit={(event) => {
				event.preventDefault();
				if (!valid) return;
				onSubmit({
					...draftToWriteInput(draft),
					scopeRef: scopeRef.trim(),
					scopeType,
				});
			}}
		>
			{scoped && (
				<>
					<Field htmlFor={`${prefix}-scope`} label="Scope">
						<Select
							id={`${prefix}-scope`}
							onChange={(e) => setScopeType(e.target.value as typeof scopeType)}
							value={scopeType}
						>
							{SCOPES.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</Select>
					</Field>
					<Field htmlFor={`${prefix}-ref`} label="Ref (Mongo id)">
						<TextInput
							id={`${prefix}-ref`}
							onChange={(e) => setScopeRef(e.target.value)}
							value={scopeRef}
						/>
					</Field>
				</>
			)}
			<Field htmlFor={`${prefix}-class`} label="Class">
				<Select
					id={`${prefix}-class`}
					onChange={(e) => patch({ classId: e.target.value })}
					value={draft.classId}
				>
					<option value="">Choose…</option>
					{classes.map((cls) => (
						<option key={cls.classId} value={cls.classId}>
							{cls.classId}
						</option>
					))}
				</Select>
			</Field>
			<Field htmlFor={`${prefix}-season`} label="Season">
				<Select
					id={`${prefix}-season`}
					onChange={(e) =>
						patch({ season: e.target.value as PriorDraft["season"] })
					}
					value={draft.season}
				>
					<option value="">any</option>
					{SEASONS.map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</Select>
			</Field>
			<Field htmlFor={`${prefix}-hours`} label="Hours">
				<Select
					id={`${prefix}-hours`}
					onChange={(e) =>
						patch({ hourBucket: e.target.value as PriorDraft["hourBucket"] })
					}
					value={draft.hourBucket}
				>
					<option value="">any</option>
					{HOUR_BUCKETS.map((h) => (
						<option key={h} value={h}>
							{h}
						</option>
					))}
				</Select>
			</Field>
			<Field htmlFor={`${prefix}-percent`} label="Prior probability (%)">
				<TextInput
					id={`${prefix}-percent`}
					inputMode="decimal"
					onChange={(e) => patch({ percent: e.target.value })}
					value={draft.percent}
				/>
			</Field>
			<span className="text-tertiary pb-1 text-xs">
				= log odds {Number.isNaN(logPriorOdds) ? "—" : logPriorOdds.toFixed(2)}
			</span>
			<Field htmlFor={`${prefix}-notes`} label="Notes">
				<TextInput
					id={`${prefix}-notes`}
					onChange={(e) => patch({ notes: e.target.value })}
					value={draft.notes}
				/>
			</Field>
			<Button disabled={busy || !valid} type="submit">
				Save prior
			</Button>
			{error && (
				<span className="text-error-primary text-sm" role="alert">
					{error.message}
				</span>
			)}
		</form>
	);
};
